import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { StressResultsService } from '../../services/stress-results.service';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { SelectItem } from 'primeng/api';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { lastValueFrom } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { DisplacementResultsUi } from '../../models/perivis-results-state.model';
import { Store } from '@ngneat/elf';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { DisplacementResultsModel } from '../../models/displacement-results.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';

@Component({
  selector: 'app-displacement-results',
  templateUrl: './displacement-results.component.html',
  styles: [],
  standalone: false
})
export class DisplacementResultsComponent implements OnInit, OnDestroy {
  private _forceUnit: string;
  private _doglegUnit: string;
  private _pressureUnit: string;
  private _temperatureUnit: string;
  private _torqueUnit: string;
  private selectedString: string;

  public displacementResults: DisplacementResultsModel[];
  public loadCases: SelectItem[] = [];
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public modes: SelectItem[] = [
    { label: 'Single', value: 'single' },
    { label: 'Multiple', value: 'multiple' }
  ];
  public errorMsg: string;
  public results: any[];
  public depthView: SelectItem[] = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];
  public isLoading = signal<boolean>(true);
  public selectedMode: string = 'single';
  public selectedResultDisplay: string = 'grid';
  public selectedDepthView: string = 'md';
  public selectedLoadCase: SelectItem = null;
  public yAxisTitle = '';
  public xAxisTitle = '';
  public unit = '(psi)';
  public tableName: string;
  public userUnits: UserUnitsModel;
  public plotName: string;
  public signalRChange: boolean;
  public columnDefs: { header: string, field: string }[];
  public columnDefinitions: any[];
  public longLengthUnit: string;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.displacementResultsStore = this._storeService.createStore(this.componentId, new DisplacementResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedMode = this.displacementResultsStore.state.selectedMode || 'single';
    this.selectedResultDisplay = this.displacementResultsStore.state.selectedResultDisplay || 'grid';
    this.selectedDepthView = this.displacementResultsStore.state.selectedDepthView || 'md';
    this.selectedLoadCase = this.displacementResultsStore.state.selectedLoadCase || null;
  }
  get componentId(): string {
    return this._componentId;
  }
  public displacementResultsStore: Store;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this._forceUnit = this.userUnits.force;
    this._doglegUnit = unitsLib[this.userUnits.doglegSeverity].symbol;
    this.longLengthUnit = this.userUnits.longLengths;
    this._pressureUnit = unitsLib[this.userUnits.pressure].symbol;
    this._temperatureUnit = this.userUnits.temperature;
    this._torqueUnit = unitsLib[this.userUnits.torque].symbol;

    this.setColumnDefinitions();
    // this.refreshResults(true, false);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.refreshResults(true, false, false);
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS 
        || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.refreshResults(true, true);
    }
  }

  public async refreshResults(isLoading: boolean, calledFromSignalR: boolean, calledFromMode?: boolean): Promise<void> {
    this.isLoading.set(isLoading);
    this.results = [];

    if ((!this.displacementResults || calledFromSignalR) && !calledFromMode) {
      try {
        this.displacementResults = await lastValueFrom(this._stressResultsService.getDisplacementResults());
        this.signalRChange = true;
      } catch {
        this.errorMsg = "Well Configuration Missing: Please specify at least one string";
        this.isLoading.set(false);
        return;
      }
    }

    if (this.displacementResults && (this.displacementResults.length === 0 || this.displacementResults[0].loadCaseName === '')) {
      this.errorMsg = "Please calculate PERIVIS to see results";
      this.columnDefs = [];
      this.results = [];
      this.isLoading.set(false);
      return;
    } else {
      this.errorMsg = null;
    }

    if (this.displacementResultsStore.state.selectedMode == "single" && this.displacementResults) {
      this.handleSingleDisplacementLoad(this.displacementResults);
    } else {
      this.handleMultiDisplacementLoad(this.displacementResults)
    }

    this.isLoading.set(false);
  }

  private formatDecimal(value: any, numDecimalPoints: number): string {
    if (isNaN(value) || value === null || value === undefined) {
      return value;
    }

    const roundedValue = Number(value.toFixed(numDecimalPoints));
    return roundedValue.toLocaleString('en-US', {
      maximumFractionDigits: numDecimalPoints
    });
  }

  private setColumnDefinitions(): void {
    this.columnDefs = this.getResultTypes('header', 'field');
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Internal Pressure (${this._pressureUnit})`, field: 'internalPressure',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `External Pressure (${this._pressureUnit})`, field: 'externalPressure',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Differential Pressure (${this._pressureUnit})`, field: 'differentialPressure',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Temperature (${this._temperatureUnit})`, field: 'temperature',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Effective Force (${this._forceUnit})`, field: 'effectiveForce',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `True Axial Force (${this._forceUnit})`, field: 'trueAxialForce',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Total Displacement (${this.longLengthUnit})`, field: 'totalDisplacement',
        valueFormatter: (params: any) => this.formatDecimal(params, this.longLengthUnit == 'm' ? 2 : 9)
      },
      {
        header: `Hooke Displacement (${this.longLengthUnit})`, field: 'hookeDisplacement',
        valueFormatter: (params: any) => this.formatDecimal(params, this.longLengthUnit == 'm' ? 2 : 9)
      },
      {
        header: `Poisson Displacement (${this.longLengthUnit})`, field: 'poissonDisplacement',
        valueFormatter: (params: any) => this.formatDecimal(params, this.longLengthUnit == 'm' ? 2 : 9)
      },
      {
        header: `Thermal Displacement (${this.longLengthUnit})`, field: 'thermalDisplacement',
        valueFormatter: (params: any) => this.formatDecimal(params, this.longLengthUnit == 'm' ? 2 : 9)
      },
      {
        header: `Dogleg (${this._doglegUnit})`, field: 'doglegSeverity',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Dogleg Due To Buckling (${this._doglegUnit})`, field: 'doglegDueToBuckling',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Torque Due To Buckling (${this._torqueUnit})`, field: 'torqueDueToBuckling',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Buckling Displacement (${this.longLengthUnit})`, field: 'bucklingDisplacement',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Critical Sinusoidal Buckling Force (${this._forceUnit})`, field: 'criticalSinusoidallBucklingForce',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: `Axial Stress (${this.userUnits?.stressPressure})`, field: 'axialStress',
        valueFormatter: (params: any) => this.formatDecimal(params, 2)
      },
      {
        header: 'Strain', field: 'strain',
        valueFormatter: (params: any) => this.formatDecimal(params, 9)
      }
    ];
  }

  onModeChange(e): void {
    this.selectedMode = e.value;
    this.displacementResultsStore.update(state => ({ ...state, selectedMode: e.value }));
    if (this.displacementResultsStore.state.selectedMode == "multiple") {
      this.selectedResultDisplay = 'plot';
      this.selectedLoadCase = { label: 'Internal Pressure', value: 'internalPressure' };
      this.displacementResultsStore.update(state => ({ ...state, selectedResultDisplay: 'plot', selectedLoadCase: { label: 'Internal Pressure', value: 'internalPressure' } }));
    } else {
      this.selectedResultDisplay = 'grid';
      this.displacementResultsStore.update(state => ({ ...state, selectedResultDisplay: 'grid' }));
      this.xAxisTitle = '';
    }
    this.plotName = this.setPlotName();
    this.refreshResults(true, false, true);
  }

  onSelectedResultDisplayUpdated(e): void {
    this.selectedResultDisplay = e.value;
    this.displacementResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
  }

  setLoadCase(e): void {
    this.selectedLoadCase = e.value;
    if (this.displacementResultsStore.state.selectedMode === "single") {
      this.displacementResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.results = e.value.value.displacementResults;
    } else {
      this.displacementResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.refreshResults(true, false, false);
    }
  }

  setPlotName(): string {
    return 'displacementPlot - ' + this.selectedString + ' - ' + this.displacementResultsStore.state.selectedLoadCase.label;
  }

  public depthViewToggle(e) {
    this.selectedDepthView = e.value;
    this.displacementResultsStore.update(state => ({ ...state, selectedDepthView: e.value }));
  }

  private handleSingleDisplacementLoad(displacementResults: any[]) {
    this.setColumnDefinitions();
    this.results = [];

    this.loadCases = displacementResults?.map(lc => {
      return { label: lc.loadCaseName, value: lc };
    }) ?? [];
    if (!this.displacementResultsStore.state.selectedLoadCase || !this.loadCases.find(l => l.value.tubularStringId == this.displacementResultsStore.state.selectedLoadCase?.value.tubularStringId)) {
      this.displacementResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
      this.selectedLoadCase = this.loadCases[0];
      this.plotName = this.setPlotName();
    }
    this.results = this.displacementResultsStore.state.selectedLoadCase?.value.displacementResults || [];
  }

  private handleMultiDisplacementLoad(displacementResults: any[]) {
    this.columnDefinitions.splice(2, this.columnDefinitions.length);
    this.columnDefs = [];
    this.results = [];

    this.yAxisTitle = `MD (${this.longLengthUnit})`;

    this.loadCases = this.getResultTypes('label', 'value');

    switch (this.displacementResultsStore.state.selectedLoadCase.value) {
      case 'internalPressure': {
        this.xAxisTitle = `Internal Pressure (${this._pressureUnit})`;
        this.unit = `(${this._pressureUnit})`;
        break;
      }
      case 'externalPressure': {
        this.xAxisTitle = `External Pressure (${this._pressureUnit})`;
        this.unit = `(${this._pressureUnit})`;
        break;
      }
      case 'differentialPressure': {
        this.xAxisTitle = `Differential Pressure (${this._pressureUnit})`;
        this.unit = `(${this._pressureUnit})`;
        break;
      }
      case 'temperature': {
        this.xAxisTitle = `Temperature (${this._temperatureUnit})`;
        this.unit = `(${this._temperatureUnit})`;
        break;
      }
      case 'trueAxialForce': {
        this.xAxisTitle = `True Axial Force (${this._forceUnit})`;
        this.unit = `(${this._forceUnit})`;
        break;
      }
      case 'axialStress': {
        this.xAxisTitle = `Axial Stress (${this._pressureUnit})`;
        this.unit = `(${this._pressureUnit})`;
        break;
      }
      case 'totalDisplacement': {
        this.xAxisTitle = `Total Displacement (${this.longLengthUnit})`;
        this.unit = `(${this.longLengthUnit})`;
        break;
      }
      case 'thermalDisplacement': {
        this.xAxisTitle = `Thermal Displacement (${this.longLengthUnit})`;
        this.unit = `(${this.longLengthUnit})`;
        break;
      }
      case 'poissonDisplacement': {
        this.xAxisTitle = `Poisson Displacement (${this.longLengthUnit})`;
        this.unit = `(${this.longLengthUnit})`;
        break;
      }
      case 'bucklingDisplacement': {
        this.xAxisTitle = `Buckling Displacement (${this.longLengthUnit})`;
        this.unit = `(${this.longLengthUnit})`;
        break;
      }
      case 'hookeDisplacement': {
        this.xAxisTitle = `Hooke Displacement (${this.longLengthUnit})`;
        this.unit = `(${this.longLengthUnit})`;
        break;
      }
      case 'strain': {
        this.xAxisTitle = 'Strain';
        this.unit = '';
        break;
      }
      case 'effectiveForce': {
        this.xAxisTitle = `Effective Force (${this._forceUnit})`;
        this.unit = `(${this._forceUnit})`;
        break;
      }
      case 'criticalSinusoidallBucklingForce': {
        this.xAxisTitle = `Critical Sinusoidal Buckling Force (${this._forceUnit})`;
        this.unit = `(${this._forceUnit})`;
        break;
      }
      case 'doglegSeverity': {
        this.xAxisTitle = `Dogleg Severity (${this._doglegUnit})`;
        this.unit = `(${this._doglegUnit})`;
        break;
      }
      case 'doglegDueToBuckling': {
        this.xAxisTitle = `Dogleg Due To Buckling (${this._doglegUnit})`;
        this.unit = `(${this._doglegUnit})`;
        break;
      }
      case 'torqueDueToBuckling': {
        this.xAxisTitle = `Torque Due To Buckling (${this._torqueUnit})`;
        this.unit = `(${this._torqueUnit})`;
        break;
      }
      default: {
        break;
      }
    }

    displacementResults.forEach(load => {
      const loadName: string = load.loadCaseName + ' ' + this.unit;
      const loadNameCamel = toCamelCaseString(load.loadCaseName);

      this.columnDefs.push({ header: load.loadCaseName, field: loadNameCamel });

      let numDecimals = 2;
      switch (this.displacementResultsStore.state.selectedLoadCase.value) {
        case 'hookeDisplacement': {
          numDecimals = this.longLengthUnit == 'm' ? 2 : 9;
          break;
        }
        case 'poissonDisplacement': {
          numDecimals = this.longLengthUnit == 'm' ? 2 : 9;
          break;
        }
        case 'totalDisplacement': {
          numDecimals = this.longLengthUnit == 'm' ? 2 : 9;
          break;
        }
        case 'thermalDisplacement': {
          numDecimals = this.longLengthUnit == 'm' ? 2 : 9;
          break;
        }
        case 'strain': {
          numDecimals = this.longLengthUnit == 'm' ? 2 : 9;
          break;
        }
        default: {
          numDecimals = 2;
          break;
        }
      }

      this.columnDefinitions.push({
        header: loadName,
        field: loadNameCamel,
        valueFormatter: (params: any) => this.formatDecimal(params, numDecimals)
      });

      const backupArray = [];

      load.displacementResults.forEach(loadResult => {
        const result = {
          [loadNameCamel]: loadResult[this.displacementResultsStore.state.selectedLoadCase.value],
          measuredDepth: loadResult.measuredDepth,
          trueVerticalDepth: loadResult.trueVerticalDepth
        }

        if (displacementResults[0].loadCaseName === load.loadCaseName) {
          this.results.push(result);
        } else {
          backupArray.push(result);
        }
      });
      this.results = this.results.map(x => {
        const otherLoad = backupArray.find(e => e.measuredDepth === x.measuredDepth)
        return { ...x, ...otherLoad }
      });
    });
    this.plotName = this.setPlotName();
  }

  private getResultTypes(labelName, valueName): any[] {
    return [
      { [labelName]: 'Internal Pressure', [valueName]: 'internalPressure' },
      { [labelName]: 'External Pressure', [valueName]: 'externalPressure' },
      { [labelName]: 'Differential Pressure', [valueName]: 'differentialPressure' },
      { [labelName]: 'Temperature', [valueName]: 'temperature' },
      { [labelName]: 'True Axial Force', [valueName]: 'trueAxialForce' },
      { [labelName]: 'Axial Stress', [valueName]: 'axialStress' },
      { [labelName]: 'Total Displacement', [valueName]: 'totalDisplacement' },
      { [labelName]: 'Thermal Displacement', [valueName]: 'thermalDisplacement' },
      { [labelName]: 'Poisson Displacement', [valueName]: 'poissonDisplacement' },
      { [labelName]: 'Buckling Displacement', [valueName]: 'bucklingDisplacement' },
      { [labelName]: 'Hooke Displacement', [valueName]: 'hookeDisplacement' },
      { [labelName]: 'Strain', [valueName]: 'strain' },
      { [labelName]: 'Effective Force', [valueName]: 'effectiveForce' },
      { [labelName]: 'Critical Sinusoidal Buckling Force', [valueName]: 'criticalSinusoidallBucklingForce' },
      { [labelName]: 'Dogleg Severity', [valueName]: 'doglegSeverity' },
      { [labelName]: 'Dogleg Due To Buckling', [valueName]: 'doglegDueToBuckling' },
      { [labelName]: 'Torque Due To Buckling', [valueName]: 'torqueDueToBuckling' }
    ];
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
